import { useEffect, useState } from 'react';

import './Style.css';
import ModalTrigger from '../../../shared/modal/ModalTrigger';
import { NumberSelect } from '../numberSelect/NumberSelect';

type MeasurementsInputProps = {
  questionTitle: string;
  modalText?: string;
  onSetValue: (value: { id: string; value: number | string }) => void;
  onOpenModal?: () => void;
  questionid: string;
  assessmentValues?: Record<string, unknown>;
  width: string;
  fadeIn?: string;
  skipText?: string;
  skipText2?: string;
  type: string;
  type1Text: string;
  type2Text: string;
};

export function MeasurementsInput({
  questionTitle,
  modalText,
  onSetValue,
  onOpenModal,
  questionid,
  assessmentValues,
  width,
  fadeIn,
  skipText,
  skipText2,
  type,
  type1Text,
  type2Text,
}: MeasurementsInputProps) {
  const [unitType, setUnitType] = useState('type1');
  const [skipActive, setSkipActive] = useState('');
  const [skipActive2, setSkipActive2] = useState('');

  function setTypeToggle(type) {
    setUnitType(type);
    saveValue(type);
  }

  function saveValue(item) {
    if (item !== 'skipped') {
      setSkipActive('');
    }
    if (item !== 'unsure') {
      setSkipActive2('');
    }
    onSetValue({ id: questionid, value: item });
  }

  function toggleSkipActive() {
    if (skipActive === 'active') {
      setSkipActive('');
      onSetValue({ id: questionid, value: '' });
      setUnitType('type1');
    } else {
      setSkipActive('active');
      setSkipActive2('');
      onSetValue({ id: questionid, value: 'skipped' });
    }
  }

  function toggleSkipActive2() {
    if (skipActive2 === 'active') {
      setSkipActive2('');
      onSetValue({ id: questionid, value: '' });
      setUnitType('type1');
    } else {
      setSkipActive2('active');
      setSkipActive('');
      onSetValue({ id: questionid, value: 'unsure' });
    }
  }

  useEffect(() => {
    if (assessmentValues[questionid]) {
      const value = assessmentValues[questionid];
      if (value === 'skipped') {
        setSkipActive('active');
      }
      if (value === 'unsure') {
        setSkipActive2('active');
      }

      if (value !== 'skipped' && value !== 'unsure') {
        setUnitType(value as string);
      }
    }
  }, [assessmentValues[questionid]]);

  const [blockOpacity, setBlockOpacity] = useState(0);

  useEffect(() => {
    setTimeout(function () {
      setBlockOpacity(1);
    }, 100);
  }, []);

  return (
    <div
      id={questionid}
      className={`measures question_block${width}`}
      style={{ opacity: fadeIn ? blockOpacity : '1' }}
    >
      <fieldset data-input-type="measurements-input">
        <legend className="assessment_question_title">{questionTitle}</legend>
        {modalText && (
          <ModalTrigger
            modalText={modalText}
            onOpenModal={onOpenModal}
            question={questionid}
            questionTitle={questionTitle}
          />
        )}

        <div className="measuresQuestionBlock">
          {skipText ? (
            <button
              type="button"
              className={`suffixLabel ${skipActive} fs-exclude`}
              onClick={toggleSkipActive}
            >
              {skipText}
            </button>
          ) : null}
          {skipText2 ? (
            <button
              type="button"
              className={`suffixLabel ${skipActive2} fs-exclude`}
              onClick={toggleSkipActive2}
            >
              {skipText2}
            </button>
          ) : null}
        </div>
        <div className="rightLabel">
          <button
            type="button"
            className={
              unitType === 'type1'
                ? 'measureSwitch measureType active'
                : 'measureSwitch measureType'
            }
            aria-pressed={unitType === 'type1'}
            id={`${questionid}settype1`}
            onClick={() => setTypeToggle('type1')}
            disabled={skipActive === 'active' || skipActive2 === 'active'}
          >
            {type1Text}
          </button>
          <button
            type="button"
            className={
              unitType === 'type2'
                ? 'measureSwitch measureType active'
                : 'measureSwitch measureType'
            }
            aria-pressed={unitType === 'type2'}
            id={`${questionid}settype2`}
            onClick={() => setTypeToggle('type2')}
            disabled={skipActive === 'active' || skipActive2 === 'active'}
          >
            {type2Text}
          </button>
        </div>
        <div className="answer_block multiple_number_select">
          {type === 'height' && unitType === 'type1' && (
            <>
              <NumberSelect
                questionid="height_ft"
                minlimit="0"
                maxlimit="8"
                trailingText="ft"
                startVal="5"
                onSetValue={onSetValue}
                assessmentValues={assessmentValues}
                disabled={skipActive === 'active' || skipActive2 === 'active'}
                controlsInfo="ft"
              />
              <NumberSelect
                questionid="height_in"
                minlimit="0"
                maxlimit="11"
                trailingText="in"
                startVal="6"
                onSetValue={onSetValue}
                assessmentValues={assessmentValues}
                disabled={skipActive === 'active' || skipActive2 === 'active'}
                controlsInfo="in"
              />
            </>
          )}
          {type === 'height' && unitType === 'type2' && (
            <NumberSelect
              questionid="height_cm"
              minlimit="0"
              maxlimit="300"
              trailingText="cm"
              startVal="160"
              onSetValue={onSetValue}
              assessmentValues={assessmentValues}
              disabled={skipActive === 'active' || skipActive2 === 'active'}
              controlsInfo="cm"
            />
          )}
          {type === 'weight' && unitType === 'type1' && (
            <>
              <NumberSelect
                questionid="weight_st"
                minlimit="0"
                maxlimit="100"
                trailingText="st"
                startVal="12"
                onSetValue={onSetValue}
                assessmentValues={assessmentValues}
                disabled={skipActive === 'active' || skipActive2 === 'active'}
                controlsInfo="st"
              />
              <NumberSelect
                questionid="weight_lb"
                minlimit="0"
                maxlimit="13"
                trailingText="lb"
                startVal="0"
                onSetValue={onSetValue}
                assessmentValues={assessmentValues}
                disabled={skipActive === 'active' || skipActive2 === 'active'}
                controlsInfo="lb"
              />
            </>
          )}
          {type === 'weight' && unitType === 'type2' && (
            <NumberSelect
              questionid="weight_kg"
              minlimit="0"
              maxlimit="500"
              trailingText="kg"
              startVal="80"
              onSetValue={onSetValue}
              assessmentValues={assessmentValues}
              disabled={skipActive === 'active' || skipActive2 === 'active'}
              controlsInfo="kg"
            />
          )}
        </div>
      </fieldset>
    </div>
  );
}
